// components/intelligence/MarketIntelligence.jsx
import React, { useEffect, useState } from 'react';
import { AlertCircle, BarChart3, TrendingUp, Target, Award, Zap } from 'lucide-react';
import DataSnapshotTimeline from './DataSnapshotTimeline';
import OverviewTab from './tabs/OverviewTab';
import TrendsTab from './tabs/TrendsTab';
import OpportunitiesTab from './tabs/OpportunitiesTab';
import CompetitiveTab from './tabs/CompetitiveTab';
import RisingStarsTab from './tabs/RisingStarsTab';
import ChatAssistant from './tabs/ChatAssistant';
import { TabButton } from './tabs/common.jsx';
import GameDetailsModal from '../discovery/GameDetailsModal';

export default function MarketIntelligence() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedTab, setSelectedTab] = useState('overview');
    const [selectedSnapshot, setSelectedSnapshot] = useState(null);
    const [selectedGameForModal, setSelectedGameForModal] = useState(null);
    const [detailsCache, setDetailsCache] = useState({});

    const [marketOverview, setMarketOverview] = useState(null);
    const [trends, setTrends] = useState(null);
    const [opportunities, setOpportunities] = useState(null);
    const [competitive, setCompetitive] = useState(null);

    useEffect(() => {
        if (selectedSnapshot) {
            fetchAllData(selectedSnapshot.date);
        } else {
            fetchAllData();
        }
    }, [selectedSnapshot?.date]);

    const fetchAllData = async (targetDate = null) => {
        setLoading(true);
        setError(null);

        try {
            const dateParam = targetDate ? `?date=${targetDate}` : '';
            const [overviewRes, trendsRes, opportunitiesRes, competitiveRes] = await Promise.all([
                fetch(`/analytics/v2/market-intelligence/overview${dateParam}`),
                fetch(`/analytics/v2/market-intelligence/trends${dateParam}`),
                fetch(`/analytics/v2/market-intelligence/opportunities${dateParam}`),
                fetch(`/analytics/v2/market-intelligence/competitive-landscape${dateParam}`)
            ]);

            if (!overviewRes.ok || !trendsRes.ok || !opportunitiesRes.ok || !competitiveRes.ok) {
                const errorDetails = {
                    overview: !overviewRes.ok ? `${overviewRes.status} ${overviewRes.statusText}` : 'OK',
                    trends: !trendsRes.ok ? `${trendsRes.status} ${trendsRes.statusText}` : 'OK',
                    opportunities: !opportunitiesRes.ok ? `${opportunitiesRes.status} ${opportunitiesRes.statusText}` : 'OK',
                    competitive: !competitiveRes.ok ? `${competitiveRes.status} ${competitiveRes.statusText}` : 'OK'
                };
                throw new Error('Failed to fetch market intelligence data: ' + JSON.stringify(errorDetails));
            }

            const [overviewData, trendsData, opportunitiesData, competitiveData] = await Promise.all([
                overviewRes.json(),
                trendsRes.json(),
                opportunitiesRes.json(),
                competitiveRes.json()
            ]);

            setMarketOverview(overviewData || {});
            setTrends(trendsData || {});
            setOpportunities(opportunitiesData || {});
            setCompetitive(competitiveData || {});
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-lg text-gray-600 font-medium">Loading Market Intelligence...</p>
                    <p className="text-sm text-gray-500 mt-2">Aggregating data from 10 datasets</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-8">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                    <div className="flex items-center">
                        <AlertCircle className="h-6 w-6 text-red-600 mr-3" />
                        <div>
                            <h3 className="text-lg font-semibold text-red-900">Error Loading Data</h3>
                            <p className="text-red-700 mt-1">{error}</p>
                        </div>
                    </div>
                    <button
                        onClick={fetchAllData}
                        className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="h-full flex bg-gradient-to-br from-gray-50 to-blue-50">
            {/* Red - Middle Content Area (Scrollable) */}
            <div className="flex-1 overflow-y-auto">
                <div className="px-6 pt-6 pb-4">
                    <h1 className="text-4xl font-bold text-gray-900 mb-2">
                        Market Intelligence Dashboard
                    </h1>
                    <p className="text-gray-600 text-lg">
                        Real-time insights from {marketOverview?.marketSize?.totalGames?.toLocaleString?.() || '—'} games across 10 datasets
                    </p>
                    <div className="mt-3 flex items-center space-x-3 text-sm text-gray-500 flex-wrap">
                        <div className="flex items-center">
                            <div className="h-2 w-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                            <span>Live Data</span>
                        </div>
                        <span className="text-gray-300">•</span>
                        <span>
                            Last updated:{' '}
                            {selectedSnapshot?.timestamp
                                ? new Date(selectedSnapshot.timestamp).toLocaleString()
                                : '—'}
                        </span>
                    </div>
                </div>

                <div className="px-6 pb-6">
                    <div className="bg-white rounded-lg shadow-sm p-1 mb-6 inline-flex flex-wrap">
                        <TabButton
                            active={selectedTab === 'overview'}
                            onClick={() => setSelectedTab('overview')}
                            icon={<BarChart3 className="h-4 w-4" />}
                            label="Market Overview"
                        />
                        <TabButton
                            active={selectedTab === 'trends'}
                            onClick={() => setSelectedTab('trends')}
                            icon={<TrendingUp className="h-4 w-4" />}
                            label="Trends & Momentum"
                        />
                        <TabButton
                            active={selectedTab === 'opportunities'}
                            onClick={() => setSelectedTab('opportunities')}
                            icon={<Target className="h-4 w-4" />}
                            label="Opportunities"
                        />
                        <TabButton
                            active={selectedTab === 'competitive'}
                            onClick={() => setSelectedTab('competitive')}
                            icon={<Award className="h-4 w-4" />}
                            label="Competitive Landscape"
                        />
                        <TabButton
                            active={selectedTab === 'rising'}
                            onClick={() => setSelectedTab('rising')}
                            icon={<Zap className="h-4 w-4" />}
                            label="Rising Stars"
                        />
                    </div>

                    {selectedTab === 'overview' && (
                        <OverviewTab data={marketOverview || {}} />
                    )}
                    {selectedTab === 'trends' && (
                        <TrendsTab data={trends || {}} onGameClick={(game) => setSelectedGameForModal(game)} />
                    )}
                    {selectedTab === 'opportunities' && (
                        <OpportunitiesTab data={opportunities || {}} />
                    )}
                    {selectedTab === 'competitive' && (
                        <CompetitiveTab data={competitive || {}} />
                    )}
                    {selectedTab === 'rising' && (
                        <RisingStarsTab onGameClick={(game) => setSelectedGameForModal(game)} />
                    )}
                </div>
            </div>

            {/* Purple - Right Sidebar (Fixed, extends to header) */}
            <div className="w-96 flex-shrink-0 flex flex-col border-l border-gray-200 bg-white/50">
                {/* Snapshot Timeline - Fixed height */}
                <div className="h-72 flex-shrink-0 border-b border-gray-200 p-4">
                    <DataSnapshotTimeline
                        onSnapshotSelect={setSelectedSnapshot}
                        currentDate={selectedSnapshot?.date}
                        selectedTab={selectedTab}
                    />
                </div>
                
                {/* Chat Assistant - Fills remaining space */}
                <div className="flex-1 min-h-0 p-4">
                    <ChatAssistant />
                </div>
            </div>

            {selectedGameForModal && (
                <GameDetailsModal
                    game={selectedGameForModal}
                    onClose={() => setSelectedGameForModal(null)}
                    detailsCache={detailsCache}
                    setDetailsCache={setDetailsCache}
                />
            )}
        </div>
    );
}
